import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchIcon from '@mui/icons-material/Search';
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import unlinkicon from "../../../../assests/unlinked.svg"
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import "./CohortAssortmentList.css";

const CohortAssortmentListComponent = (props) => {
  const navigate = useNavigate();
  const [openRuleDialog, setOpenRuleDialog] = useState(false);
  const [selectedRuleFailures, setSelectedRuleFailures] = useState([]);
  const [selectedWarehouseName, setSelectedWarehouseName] = useState("");

  const {
    assortmentCohortMetaDataList,
    handleWarehouseClick,
    loading,
    error,
    searchTerm,
    onSearchChange,
    onClearSearch,
    totalCount,
    hasMore,
    onLoadMore
  } = props;

  const formatDate = (dateString) => {
    if (!dateString || dateString === "null") return "Not set";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid date";
      
      const day = date.getDate();
      const suffix = getNumberSuffix(day);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day}${suffix} ${month} ${year}`;
    } catch (error) {
      return "Invalid date";
    }
  };

  const getNumberSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  // Function to get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/${imagePath}`;
  };

  // Function to get failure message based on failure type
  const getFailureMessage = (failure) => {
    const failureType = failure.failureType;
    const failedItems = failure.failedItems || {};

    switch (failureType) {
      case "variant_not_present":
        return {
          title: "Variant Not Present",
          message: `Variant${failedItems.failRuleItemDetails?.length > 1 ? 's' : ''} ${failedItems.failRuleItemDetails?.map(item => item.name).join(', ')} ${failedItems.failRuleItemDetails?.length > 1 ? 'are' : 'is'} missing in assortment`,
          icon: <ErrorOutlineIcon className="failure-icon variant" />,
          color: "#EF4444"
        };
      
      case "product_not_present":
        return {
          title: "Product Not Present",
          message: `Product${failedItems.failRuleItemDetails?.length > 1 ? 's' : ''} ${failedItems.failRuleItemDetails?.map(item => item.name).join(', ')} ${failedItems.failRuleItemDetails?.length > 1 ? 'are' : 'is'} missing in assortment`,
          icon: <ErrorOutlineIcon className="failure-icon product" />,
          color: "#EF4444"
        };
      
      case "brand_not_present":
        return {
          title: "Brand Not Present",
          message: `Brand${failedItems.failRuleItemDetails?.length > 1 ? 's' : ''}: ${failedItems.failRuleItemDetails?.map(item => item.name).join(', ')} ${failedItems.failRuleItemDetails?.length > 1 ? 'are' : 'is'} missing in assortment`,
          icon: <ErrorOutlineIcon className="failure-icon brand" />,
          color: "#EF4444"
        };
      
      case "mv_not_present":
        return {
          title: "MV Not Present",
          message: `MV${failedItems.failRuleItemDetails?.length > 1 ? 's' : ''} ${failedItems.failRuleItemDetails?.map(item => item.name).join(', ')} ${failedItems.failRuleItemDetails?.length > 1 ? 'are' : 'is'} not present in assortment`,
          icon: <ErrorOutlineIcon className="failure-icon mv" />,
          color: "#EF4444"
        };
      
      case "offer_price_breached":
        return {
          title: "Offer Price Breached",
          message: `Offer price limit of ₹${failedItems.maxOfferPrice} has been breached`,
          icon: <WarningAmberIcon className="failure-icon price" />,
          color: "#F59E0B"
        };
      
      case "item_restricted":
        return {
          title: "Item Restricted",
          message: `Item${failedItems.failRuleItemDetails?.length > 1 ? 's' : ''} ${failedItems.failRuleItemDetails?.map(item => item.name).join(', ')} ${failedItems.failRuleItemDetails?.length > 1 ? 'are' : 'is'} restricted`,
          icon: <WarningAmberIcon className="failure-icon restricted" />,
          color: "#F59E0B"
        };
      
      case "brand_restricted":
        return {
          title: "Brand Restricted",
          message: `Brand${failedItems.failRuleItemDetails?.length > 1 ? 's' : ''} ${failedItems.failRuleItemDetails?.map(item => item.name).join(', ')} ${failedItems.failRuleItemDetails?.length > 1 ? 'are' : 'is'} restricted`,
          icon: <WarningAmberIcon className="failure-icon restricted" />,
          color: "#F59E0B"
        };
      
      case "product_restricted":
        return {
          title: "Product Restricted",
          message: `Product${failedItems.failRuleItemDetails?.length > 1 ? 's' : ''} ${failedItems.failRuleItemDetails?.map(item => item.name).join(', ')} ${failedItems.failRuleItemDetails?.length > 1 ? 'are' : 'is'} restricted`,
          icon: <WarningAmberIcon className="failure-icon restricted" />,
          color: "#F59E0B"
        };
      
      case "variant_restricted":
        return {
          title: "Variant Restricted",
          message: `Variant${failedItems.failRuleItemDetails?.length > 1 ? 's' : ''} ${failedItems.failRuleItemDetails?.map(item => item.name).join(', ')} ${failedItems.failRuleItemDetails?.length > 1 ? 'are' : 'is'} restricted`,
          icon: <WarningAmberIcon className="failure-icon restricted" />,
          color: "#F59E0B"
        };
      
      case "mv_restricted":
        return {
          title: "MV Restricted",
          message: `MV${failedItems.failRuleItemDetails?.length > 1 ? 's' : ''} ${failedItems.failRuleItemDetails?.map(item => item.name).join(', ')} ${failedItems.failRuleItemDetails?.length > 1 ? 'are' : 'is'} restricted`,
          icon: <WarningAmberIcon className="failure-icon restricted" />,
          color: "#F59E0B"
        };
      
      default:
        return {
          title: "Rule Failed",
          message: "This rule has failed validation",
          icon: <InfoIcon className="failure-icon default" />,
          color: "#6B7280"
        };
    }
  };

  // Format IDs from failRuleItemDetails for display
  const formatItemIds = (failRuleItemDetails) => {
    if (!failRuleItemDetails || failRuleItemDetails.length === 0) return "N/A";
    const ids = failRuleItemDetails.map(item => item.id).filter(id => id);
    if (ids.length === 0) return "N/A";
    if (ids.length === 1) return ids[0].toString();
    return `${ids.slice(0, 3).join(', ')}${ids.length > 3 ? ` +${ids.length - 3} more` : ''}`;
  };

  // Get item names for display
  const getItemNames = (failRuleItemDetails) => {
    if (!failRuleItemDetails || failRuleItemDetails.length === 0) return "N/A";
    const names = failRuleItemDetails.map(item => item.name).filter(name => name);
    if (names.length === 0) return "N/A";
    if (names.length === 1) return names[0];
    return `${names.slice(0, 2).join(', ')}${names.length > 2 ? ` +${names.length - 2} more` : ''}`;
  };

  const getRuleStatus = (item) => {
    const totalRules = item.total_rules || 0;
    const ruleFailures = item.rule_failure_details || [];
    const failedCount = ruleFailures.length;

    if (failedCount === 0 && totalRules > 0) {
      return {
        text: "All Pass",
        className: "cohort-all-pass"
      };
    }

    if (failedCount > 0) {
      return {
        text: `${failedCount.toString().padStart(2, '0')} Fail`,
        className: "cohort-some-fail",
        isClickable: true
      };
    }

    return {
      text: "No Rules",
      className: "cohort-no-rules",
      isClickable: false
    };
  };

  const handleCardHeaderClick = (e, reference_id) => {
    e.stopPropagation();
    handleWarehouseClick(reference_id);
  };

  const handleRuleClick = (e, item) => {
    e.stopPropagation();
    setSelectedRuleFailures(item.rule_failure_details || []);
    setSelectedWarehouseName(item.reference_name);
    setOpenRuleDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenRuleDialog(false);
    setSelectedRuleFailures([]);
    setSelectedWarehouseName("");
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSearchChange(e);
    }
  };

  if (loading && assortmentCohortMetaDataList.length === 0) {
    return (
      <div className="cohort-list-page">
        <Sidebar />
        <div className="cohort-list-content">
          <div className="cohort-loading-container">
            <CircularProgress size={60} thickness={4} />
            <p>Loading cohorts...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cohort-list-page">
        <Sidebar />
        <div className="cohort-list-content">
          <div className="cohort-error-container">
            <div className="cohort-error-icon">!</div>
            <h3>Error loading data</h3>
            <p>{error}</p>
            <button 
              className="cohort-retry-btn" 
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cohort-list-page">
      <Sidebar />
      <div className="cohort-list-content">
        <div className="cohort-list-header">
          <div className="cohort-header-left">
            <button className="cohort-back-btn" onClick={() => navigate("/")}>
              ← 
            </button>
            <div className="cohort-header-title-section">
              <h1 className="cohort-list-title">Cohort</h1>
            </div>
          </div>
        </div>

        <div className="cohort-list-search">
          <SearchIcon className="cohort-list-search-icon" />
          <input
            type="text"
            placeholder="Search Cohort by Name, ID, Machine Name, Id"
            value={searchTerm}
            onChange={onSearchChange}
            onKeyPress={handleSearchKeyPress}
            className="cohort-list-search-input"
          />
          {searchTerm && (
            <button 
              className="cohort-clear-search-btn"
              onClick={onClearSearch}
            >
              ✕
            </button>
          )}
        </div>

        <div className="cohort-list-grid">
          {assortmentCohortMetaDataList && assortmentCohortMetaDataList.length > 0 ? (
            <>
              {assortmentCohortMetaDataList.map((item) => {
                const ruleStatus = getRuleStatus(item);
                const activeStartDate = formatDate(item.active_assortment_start_date);
                const scheduledStartDate= formatDate(item.schedule_assortment_start_date)
                const isLinked = item.linked_warehouse_name && item.linked_warehouse_name !== "Unlinked";
                const scheduledCount = item.scheduled_assortments || 'N/A';
                
                return (
                  <div
                    className="cohort-list-card"
                    key={item.reference_id}
                  >
                    {/* Clickable Card Header with Location Icon */}
                    <div 
                      className="cohort-card-header clickable"
                      onClick={(e) => handleCardHeaderClick(e, item.reference_id)}
                    >
                      <div className="cohort-card-header-content">
                        <div className="cohort-name-with-icon">
                          <LocationOnIcon className="cohort-location-icon" />
                          <h3 className="cohort-name">{item.reference_name}</h3>
                        </div>
                        <ChevronRightIcon className="cohort-card-arrow-icon" />
                      </div>
                      
                     
                      {isLinked ? (
                        <div className="cohort-linked-text">
                          Linked with {item.linked_warehouse_name}
                        </div>
                      ) : (
                        <div className="cohort-unlinked-text">
                          <img src={unlinkicon} className="cohort-unlinked-icon" alt="Unlinked" />
                          Unlinked
                        </div>
                      )}
                    </div>
                    
                    <div className="cohort-status-container">
                      {/* Active Status - Exact match with screenshot */}
                      <div className="cohort-status-item">
                        <div className="cohort-status-label-section">
                          <span className="cohort-status-bullet active"></span>
                          <span className="cohort-status-label-active">Active</span>
                        </div>
                        <div className="cohort-status-date-section">
                          <span className="cohort-status-date-text">
                            Start Date: <strong>{activeStartDate}</strong>
                          </span>
                        </div>
                      </div>
                      
                      {/* Scheduled Status - Exact match with screenshot */}
                      <div className="cohort-status-item">
                        <div className="cohort-status-label-section">
                          <span className="cohort-status-bullet scheduled"></span>
                          <span className="cohort-status-label-scheduled">Scheduled</span>
                        </div>
                       
                          <div className="cohort-status-date-section">
                          <span className="cohort-status-date-text">
                            Start Date: <strong>{scheduledStartDate || 'Not set'}</strong>
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Applied Rules Section */}
                    <div className="cohort-rules-section">
                      <span className="cohort-rules-label">Applied Rules:</span>
                      <span className="cohort-rules-number">{item.total_rules?.toString().padStart(2, '0') || '00'}</span>
                      <span className="cohort-rules-separator">–</span>
                      <div 
                        className={`cohort-rules-status-badge ${ruleStatus.className} ${ruleStatus.isClickable ? 'cohort-clickable-rule' : ''}`}
                        onClick={(e) => ruleStatus.isClickable ? handleRuleClick(e, item) : null}
                      >
                        {ruleStatus.text}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Load More Button */}
              {hasMore && (
                <div className="cohort-load-more-container">
                  <button 
                    className="cohort-load-more-btn"
                    onClick={onLoadMore}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <CircularProgress size={20} thickness={4} />
                        <span>Loading...</span>
                      </>
                    ) : (
                      'Load More'
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="cohort-no-results">
              <div className="cohort-no-results-icon">👥</div>
              <h3>No cohorts found</h3>
              <p>{searchTerm ? `No results for "${searchTerm}"` : "No cohorts available"}</p>
              {searchTerm && (
                <button 
                  className="cohort-clear-search-btn-large"
                  onClick={onClearSearch}
                >
                  Clear Search
                </button>
              )}
            </div>
          )}
        </div>

        {/* Rule Failure Details Dialog */}
        <div className={`cohort-rule-dialog-overlay ${openRuleDialog ? 'active' : ''}`} onClick={handleCloseDialog}>
          <div className="cohort-rule-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="cohort-rule-dialog-header">
              <div className="cohort-rule-dialog-title">
                <LocationOnIcon className="cohort-rule-dialog-icon" />
                <div>
                  <h2>{selectedWarehouseName}</h2>
                  <p>{selectedRuleFailures.length} Rule{selectedRuleFailures.length !== 1 ? 's' : ''} Failed</p>
                </div>
              </div>
              <button className="cohort-rule-dialog-close" onClick={handleCloseDialog}>
                <CloseIcon />
              </button>
            </div>

            <div className="cohort-rule-dialog-content">
              <div className="cohort-rule-table-container">
                <table className="cohort-rule-table">
                  <thead>
                    <tr>
                      <th>Rule ID</th>
                      <th>Type</th>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Status Message</th>
                      <th>Image</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRuleFailures.map((failure, index) => {
                      const failureInfo = getFailureMessage(failure);
                      const failedItems = failure.failedItems || {};
                      const failRuleItemDetails = failedItems.failRuleItemDetails || [];
                      const firstItem = failRuleItemDetails[0] || {};
                      const imageUrl = getImageUrl(firstItem.image);
                      
                      return (
                        <tr key={index}>
                          <td>
                            <span className="cohort-rule-id">{failure.ruleId || 'N/A'}</span>
                          </td>
                          <td>
                            <span className={`cohort-rule-type ${failure.failureType}`}>
                              {failure.ruleType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <span className="cohort-rule-ids">
                              {/* FIXED: Use failRuleItemDetails to get IDs */}
                              {formatItemIds(failRuleItemDetails)}
                            </span>
                          </td>
                          <td>
                            <span className="cohort-rule-item-name">
                              {getItemNames(failRuleItemDetails)}
                            </span>
                          </td>
                          <td>
                            <div className="cohort-rule-message">
                              {/* <span className={`cohort-status-indicator ${failure.failureType}`}></span> */}
                              <span className="cohort-message-text">{failureInfo.message}</span>
                            </div>
                          </td>
                          <td>
                            {imageUrl ? (
                              <div className="cohort-rule-image-container">
                                <img 
                                  src={imageUrl}
                                  alt={firstItem.name || "product"}
                                  className="cohort-rule-thumbnail"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://via.placeholder.com/40x40?text=No+Image';
                                  }}
                                />
                              </div>
                            ) : (
                              <div className="cohort-rule-image-placeholder">
                                <span>📷</span>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="cohort-rule-dialog-footer">
              <button className="cohort-rule-save-btn" onClick={handleCloseDialog}>
               Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CohortAssortmentListComponent;